import React, { useEffect, useState } from 'react';
import { trainingAPI } from '@api/TrainingAPI';
import { Pagination, PaginationProps, DatePicker, Button, Select, Typography, Tooltip, message, Collapse, Tag } from 'antd';
import { useSelector } from "react-redux";
import '../../Styles/styles-ActivityLog.css';


interface HoverableTextProps {
    children: React.ReactNode;
    onClick: () => void;
}

const HoverableText: React.FC<HoverableTextProps> = ({ children, onClick }) => {

    return (
        <div className='hovererable-box' onClick={onClick} style={{ display: 'inline-block' }}>
            {children}
        </div>
    );
};

export const ActivityLog = () => {
    const { RangePicker } = DatePicker;
    const { Panel } = Collapse;

    const {
        info: {
            coach_list: coachList,
        },
        misc: { activity_types: activityTypes },
    } = useSelector(({ team }) => team);

    const [data, setData] = useState<IActivityResponse | undefined>(undefined)
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(100)
    const [startDate, setStartDate] = useState<string | undefined>(undefined)
    const [endDate, setEndDate] = useState<string | undefined>(undefined)
    const [activityDropdown, setActivityDropdown] = useState<string[] | undefined>(undefined)
    const [userId, setUserId] = useState<string[] | undefined>(undefined)
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        getActivityLog(currentPageNumber, activityDropdown, userId, startDate, endDate, pageSize)
    }, [currentPageNumber, pageSize])

    const getActivityLog = async (page: number, activity: string[] | undefined, userId: string[] | undefined, startDate: string | undefined, endDate: string | undefined, pageSizeParam: number = 10) => {
        setLoading(true)
        let data: IActivityResponse | undefined

        try {
            data = await trainingAPI.getActivityLog(page, activity, userId, startDate, endDate, pageSizeParam)
        } catch (err) {
            console.error("Error fetching activity log:", err);
            message.error("Failed to fetch activity log. Please try again later.");
            setData(undefined);
            setLoading(false);
        }

        // combine common events
        const prev: { user: number | undefined; action: string | undefined } = { user: undefined, action: undefined }
        let dataModified: IActivityData[] | undefined = undefined

        data?.results.forEach((activity) => {
            if (activity.action === prev.action && activity.user === prev.user) {
                if (dataModified) {
                    let currentEntry = dataModified[dataModified.length - 1]

                    if (currentEntry.activity) {
                        currentEntry.activity.push(activity)
                    } else {
                        currentEntry.activity = [activity]
                    }
                } else {
                    console.error("Prev activity entry not found")
                }
            } else {
                if (dataModified) {
                    dataModified.push(activity)
                } else {
                    dataModified = [activity]
                }
            }

            prev.action = activity.action
            prev.user = activity.user
        })

        if (data && data.results && dataModified) {
            data.results = dataModified
        }

        setData(data)
        setLoading(false);
    }

    const onChangePagination: PaginationProps['onChange'] = (pageNumber) => {
        setCurrentPageNumber(pageNumber)
    };

    const onShowSizeChange: PaginationProps['onShowSizeChange'] = (_, size) => {
        setCurrentPageNumber(1)
        setPageSize(size)
    };

    const onChangeRangePicker = (date: any, dateString: string[]) => {
        const [startDateLocal, endDateLocal] = dateString
        setStartDate(startDateLocal)
        setEndDate(endDateLocal)
    };

    const handleChangeCoach = (value: string[]) => {
        const userIds = coachList?.map((coach: any) => {
            const name = `${coach.first_name} ${coach.last_name}`

            if (value.includes(name)) {
                return coach?.user_id || undefined
            }

            return undefined
        }).filter((e: any) => e)

        setUserId(userIds)
    }

    const onSubmit = () => {
        getActivityLog(currentPageNumber, activityDropdown, userId, startDate, endDate, pageSize)
    }

    return (
        <>
            <div style={{ paddingBottom: '20px', columnGap: '20px', display: 'flex' }}>
                <RangePicker onChange={onChangeRangePicker} style={{ width: '700px' }} />

                <Select
                    mode="multiple"
                    allowClear
                    size={'middle'}
                    placeholder="Filter by activity"
                    defaultValue={[]}
                    value={activityDropdown}
                    onChange={setActivityDropdown}
                    style={{ width: '100%' }}
                    options={activityTypes?.map((activity: string) => {
                        return {
                            value: activity,
                            label: activity
                        }
                    }) || []}
                    maxTagCount={'responsive'}
                />

                <Select
                    mode="multiple"
                    size={'middle'}
                    allowClear
                    placeholder="Filter by coach"
                    defaultValue={[]}
                    value={coachList?.map((coach: any) => {
                        if (userId?.includes(coach.user_id)) {
                            return `${coach.first_name} ${coach.last_name}`
                        }
                    }).filter((e: any) => e)}
                    onChange={handleChangeCoach}
                    style={{ width: '100%' }}
                    options={coachList?.map((coach: any) => {
                        return {
                            value: `${coach.first_name} ${coach.last_name}`,
                            label: `${coach.first_name} ${coach.last_name}`
                        }
                    }) || []}
                    maxTagCount={'responsive'}
                />

                <Button type="primary" onClick={onSubmit} loading={loading}>Apply Filter</Button>
            </div>

            <div style={{ paddingBottom: '20px' }}>
                <Pagination showQuickJumper showSizeChanger current={currentPageNumber} pageSize={pageSize} total={data?.total || 10} onChange={onChangePagination} onShowSizeChange={onShowSizeChange} />
            </div>

            <div style={{ display: 'flex', height: '10px' }}>
                <div style={{ width: '40px' }}></div>
                <p style={{ width: '200px', fontWeight: 'bold' }}>Timestamp</p>
                <p style={{ fontWeight: 'bold' }}>Activity</p>
            </div>
            <hr />

            {data?.results.length == 0 &&
                <p>No activity present</p>
            }

            <Collapse ghost>
                {data?.results?.map((activity) => {
                    const { activity: _, ...activityParent } = activity
                    return (
                        <>
                            {activity.activity ?
                                <Panel key={activity.id} header={
                                    <ActivityView activity={activity} handleChangeCoach={handleChangeCoach} setActivityDropdown={setActivityDropdown} />} className='ant-collapse-item-no-spacing'
                                >
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ width: '24px' }}></div>
                                        <ActivityView activity={activityParent} handleChangeCoach={handleChangeCoach} setActivityDropdown={setActivityDropdown} />
                                    </div>
                                    {activity?.activity?.map((activityChild) => {
                                        return (
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '24px' }}></div>
                                                <ActivityView activity={activityChild} handleChangeCoach={handleChangeCoach} setActivityDropdown={setActivityDropdown} />
                                            </div>
                                        )
                                    })}
                                </Panel>
                                :
                                <div style={{ display: 'flex' }}>
                                    <div style={{ width: '40px' }}></div>
                                    <ActivityView activity={activity} handleChangeCoach={handleChangeCoach} setActivityDropdown={setActivityDropdown} />
                                </div>
                            }
                        </>
                    )
                })}
            </Collapse>
        </>
    )
}

const ActivityView = ({
    activity,
    handleChangeCoach,
    setActivityDropdown
}: {
    activity: IActivityData,
    handleChangeCoach: (x: string[]) => void,
    setActivityDropdown: React.Dispatch<string[] | undefined>
}) => {
    const { Panel } = Collapse;

    const [ellipsis, setEllipsis] = useState(false);

    return (
        <div style={{ display: 'flex' }} key={activity.id}>
            <Typography.Text style={{ width: '200px' }}>{new Date(activity.created_at).toLocaleString()}</Typography.Text>

            <div>
                {activity.action.toLowerCase().includes('submitted a wellness survey') ? (
                    <div style={{ paddingRight: '5px', display: 'inline-block' }}>
                        <i>{activity.action}</i>
                    </div>
                ) : (
                    <>
                        <div style={{ paddingRight: '5px', display: 'inline-block' }}>
                            <HoverableText
                                children={
                                    <Tooltip title="Click to filter by this user">
                                        {activity.first_name} {activity.last_name}
                                    </Tooltip>
                                }
                                onClick={() => handleChangeCoach([`${activity.first_name} ${activity.last_name}`])}
                            />
                        </div>

                        <div style={{ paddingRight: '5px', display: 'inline-block' }}>
                            <HoverableText
                                children={
                                    <Tooltip title="Click to filter by this activity">
                                        <i>{activity.action}</i>
                                    </Tooltip>
                                }
                                onClick={() => setActivityDropdown([activity.action])}
                            />
                        </div>
                    </>
                )}

                {activity.activity && `(${activity.activity.length + 1} times)`}

                <>
                    {(!activity.activity && activity.misc && Object.keys(activity.misc.content || {}).length !== 0) &&
                        <>
                            <HoverableText
                                children={
                                    <span style={{ color: '#1890ff' }}>
                                        [{ellipsis ? 'Show Less' : 'Show More'}]
                                    </span>
                                }
                                onClick={() => setEllipsis(!ellipsis)}
                            />

                            <Collapse
                                activeKey={ellipsis ? ['1'] : []}
                                bordered={false}
                                style={{ backgroundColor: '#e5f3fd', borderRadius: '5px' }}
                                className="collapse-no-header"
                            >
                                <Panel key="1" header={<span style={{ display: 'none' }}>Hidden</span>} showArrow={false}>
                                    {activity.misc.content?.map((content, idx) => {
                                        if (content.type === 'diff') {
                                            return (
                                                <div key={`${activity.id}-${content.name}-${idx}`} style={{ paddingTop: '2px', paddingBottom: '2px' }}>
                                                    <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                                                        {(content.name || "").split("_").join(" ")}:
                                                    </span>
                                                    <Tag style={{ margin: '0px 4px', borderRadius: '5px' }}>
                                                        {content.prev_val?.toLocaleString() ?? "None"}
                                                    </Tag>
                                                    <span> → </span>
                                                    <Tag color="green" style={{ margin: '0px 4px', borderRadius: '5px' }}>
                                                        {content.curr_val?.toLocaleString() ?? "None"}
                                                    </Tag>
                                                </div>
                                            )
                                        }

                                        if (content.type === 'html') {
                                            return (
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: content.value || "<></>" }}
                                                />
                                            )
                                        }

                                        if (content.type === 'title') {
                                            return (
                                                <b style={{marginBottom: '2px', display: 'block'}}>{content.value}</b>
                                            )
                                        }

                                        if (content.type === "text") {
                                            return (
                                                <p style={{marginBottom: '0px'}}>{content.value}</p>
                                            )
                                        }
                                    })}
                                </Panel>
                            </Collapse>
                        </>
                    }
                </>
            </div>
        </div>
    )
}

interface IActivityData {
    action: string;
    created_at: string;
    duration: number;
    first_name: string;
    http_method: string;
    id: string;
    last_name: string;
    misc: {
        prev_state?: any;
        new_state?: any;
        content?: {
            type: "title" | "diff" | "html" | "text"
            name?: string;
            prev_val?: any;
            curr_val?: any;
            value?: string;
        }[]
    };
    path: string;
    request: {
        bodyParam?: object;
        queryParams?: object;
    }
    status_code: number;
    team: number;
    user: number;
    activity?: IActivityData[]
}

interface IActivityResponse {
    page: number;
    page_size: number;
    results: IActivityData[]
    total: number;
    total_pages: number
}